import { CHECK_TIMEOUT_MS } from '../consts/HealthProbeConstants'
import { TimeoutCheckResult } from '../types/HealthProbeTypes'
import { checkWithTimeout } from './CheckWithTimeout'

/**
 * Checks if the external API Gateway is available and responding.
 * Verifies connectivity to the backend API Gateway by calling its health endpoint.
 * Requires NEXT_PUBLIC_API_GATEWAY_BASEURL to be configured.
 *
 * @returns Promise resolving to a TimeoutCheckResult indicating if the API Gateway is reachable
 * @throws Error if API Gateway URL is not configured in environment variables
 */
export const checkApiGateway = async (gatewayBaseURL?: string): Promise<TimeoutCheckResult> => {
    return checkWithTimeout('API Gateway', async () => {
        const apiGatewayUrl = gatewayBaseURL

        if (!apiGatewayUrl) {
            throw new Error('API Gateway URL not configured')
        }

        const healthUrl = `${apiGatewayUrl}/health/live`

        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS)

        try {
            const response = await fetch(healthUrl, {
                method: 'GET',
                signal: controller.signal,
                cache: 'no-store',
            })

            clearTimeout(timeoutId)

            if (!response.ok) {
                throw new Error(`API Gateway returned status ${response.status}`)
            }
        } catch (error) {
            clearTimeout(timeoutId)
            if (error instanceof Error && error.name === 'AbortError') {
                throw new Error('Request timeout')
            }
            throw error
        }
    })
}
